import { create } from 'zustand';

/**
 * 즐겨찾기 store — 2026-08-18 사용자 지시.
 *
 *   "우측메뉴의 즐겨찾기를 제일 하단으로 이동하고 즐겨찾기를 누르면 팝업이 나오고
 *    지금까지 등록된 즐겨찾기가 나오거나 새로 등록할 수 있어야 한다."
 *
 * 담는 것은 **좌측 레일 메뉴(① 1차 메뉴)의 바로가기**다 — all-frame.html 4b(ERP) 캡션의
 * "우측 레일 — 자주 쓰는 메뉴 즐겨찾기로 전환" 과 같은 의미. 즉 즐겨찾기는 새 개체가 아니라
 * **이미 있는 메뉴를 가리키는 포인터**이고, 그래서 저장 단위도 경로(`to`) 하나면 충분하다.
 *
 * 왜 서버가 아니라 localStorage 인가:
 *   1) 이 앱의 다른 개인 선호(사이드바 접힘·폭 · 미디어 탭/정렬 · 캘린더 셸 폭)가 모두 같은 방식이다.
 *   2) 서버 저장은 공유 백엔드 + prod DB 마이그레이션을 건드린다(CLAUDE.md 배포 지침). 화면 기능
 *      하나 때문에 그 위험을 지지 않는다 — 필요해지면 이 store 의 read/write 두 함수만 API 로
 *      바꾸면 되도록 경계를 좁게 뒀다.
 *   ⚠ 그래서 **브라우저(기기)마다 따로** 남는다. 기기 간 동기화는 서버 저장이 붙어야 생긴다.
 *
 * 저장 키는 사용자별로 나눈다 — 공용 PC 에서 계정을 바꿔 로그인했을 때 남의 즐겨찾기가 보이면 안 된다.
 * 팝오버 열림 상태는 영속하지 않는다(새로고침 후 떠 있으면 방해된다 — profileUiStore 와 같은 판단).
 */

/** 즐겨찾기 1건 — 식별자는 경로(`to`) 다. 레지스트리 menu_key 는 배포마다 url 이 바뀔 수 있어 쓰지 않는다. */
export interface FavoriteItem {
  /** 이동 경로(내부 라우트) 또는 외부 URL. 중복 판정 기준. */
  to: string;
  /** 등록 시점의 라벨 — 메뉴 레지스트리를 못 읽는 상황에서도 목록이 비어 보이지 않게 하는 폴백. */
  label: string;
  /** 외부 링크면 true — 새 탭으로 연다(메뉴 레지스트리 is_external 과 같은 의미). */
  external?: boolean;
}

/** 등록 상한 — 레일 옆 팝오버(288px)에서 스크롤 없이 훑을 수 있는 선. */
export const FAVORITES_MAX = 12;

const LS_PREFIX = 'wc.favorites.v1';

function lsKey(userId: string | number | null): string {
  return userId == null ? `${LS_PREFIX}.anon` : `${LS_PREFIX}.u${userId}`;
}

/** 저장값 로드 — 비정상/미저장/localStorage 비활성이면 빈 목록(기능만 조용히 죽는다). */
function readStored(userId: string | number | null): FavoriteItem[] {
  try {
    const raw = localStorage.getItem(lsKey(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((v): v is FavoriteItem =>
        !!v && typeof v === 'object'
        && typeof (v as FavoriteItem).to === 'string' && (v as FavoriteItem).to.length > 0
        && typeof (v as FavoriteItem).label === 'string')
      .slice(0, FAVORITES_MAX)
      .map((v) => ({ to: v.to, label: v.label, ...(v.external ? { external: true } : {}) }));
  } catch {
    return [];
  }
}

function writeStored(userId: string | number | null, items: FavoriteItem[]): void {
  try {
    localStorage.setItem(lsKey(userId), JSON.stringify(items));
  } catch {
    /* localStorage 비활성(프라이빗 모드 등) — 무시, 세션 내 동작은 유지 */
  }
}

interface FavoritesState {
  /** 팝오버 열림 여부(영속하지 않음). */
  open: boolean;
  /** 현재 목록이 어느 사용자 것인지 — 계정이 바뀌면 hydrate 가 다시 읽는다. */
  userId: string | number | null;
  /** hydrate 를 한 번이라도 했는지 — 로그인 전(userId=null) 상태와 구분하기 위한 표식. */
  hydrated: boolean;
  items: FavoriteItem[];

  openPopover: () => void;
  close: () => void;
  toggle: () => void;

  /** 사용자 확정 시점에 저장값을 읽어 온다. 같은 사용자면 no-op(재마운트마다 덮어쓰지 않는다). */
  hydrate: (userId: string | number | null) => void;
  /** 등록 — 이미 있으면 무시, 상한을 넘기면 무시. */
  add: (item: FavoriteItem) => void;
  /** 해제 — 경로 기준. */
  remove: (to: string) => void;
}

export const useFavoritesStore = create<FavoritesState>((set, get) => ({
  open: false,
  userId: null,
  hydrated: false,
  items: [],

  openPopover: () => set({ open: true }),
  close: () => set({ open: false }),
  toggle: () => set((s) => ({ open: !s.open })),

  hydrate: (userId) => {
    const s = get();
    if (s.hydrated && s.userId === userId) return;
    set({ userId, hydrated: true, items: readStored(userId) });
  },

  add: (item) => {
    const s = get();
    if (!item.to) return;
    if (s.items.some((f) => f.to === item.to)) return;
    if (s.items.length >= FAVORITES_MAX) return;
    const items = [...s.items, {
      to: item.to,
      label: item.label,
      ...(item.external ? { external: true as const } : {}),
    }];
    writeStored(s.userId, items);
    set({ items });
  },

  remove: (to) => {
    const s = get();
    const items = s.items.filter((f) => f.to !== to);
    if (items.length === s.items.length) return;
    writeStored(s.userId, items);
    set({ items });
  },
}));

/** 테스트 전용 — store 초기화(모듈 싱글턴이라 케이스 간 상태가 샌다). */
export function _resetFavoritesForTests(): void {
  useFavoritesStore.setState({ open: false, userId: null, hydrated: false, items: [] });
}
